import {
    areSearchParamsEqual,
    USERS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type UsersFilterItem = { key: string; value: string };

export type UsersUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    usersActiveTableToggle: boolean;
    filterUserId: UsersFilterItem | null;
    filterUsername: UsersFilterItem | null;
    filterUserBranch: UsersFilterItem | null;
    filterUserOwner: UsersFilterItem | null;
    filterTrunkNumber: UsersFilterItem | null;
    filterUserRole: UsersFilterItem[] | null;
    filterUserStatusId: UsersFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type UsersUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    usersActiveTableToggle: boolean;
    filterUserId: UsersFilterItem | null;
    filterUsername: UsersFilterItem | null;
    filterUserBranch: UsersFilterItem | null;
    filterUserOwner: UsersFilterItem | null;
    filterTrunkNumber: UsersFilterItem | null;
    filterUserRole: UsersFilterItem[] | null;
    filterUserStatusId: UsersFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialUsersUrlState(): UsersUrlViewState {
    return parseUsersUrlState(getHashSearchParams());
}

export function parseUsersUrlState(params: URLSearchParams): UsersUrlViewState {
    params = unwrapEncodedSearchParams(params, USERS_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const usersActiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const fullName = params.get('fullName');
    const branchName = params.get('branchName');
    const ownerName = params.get('ownerName');
    const trunkNumbers = params.get('trunkNumbers');
    const role = getMultiParams(params, 'role').map(toNumberValue);
    const status = getMultiParams(params, 'status').map(toNumberValue);

    if (id) uiFilters.id = id;
    if (fullName) uiFilters.fullName = fullName;
    if (branchName) uiFilters.branchName = branchName;
    if (ownerName) uiFilters.ownerName = ownerName;
    if (trunkNumbers) uiFilters.trunkNumbers = trunkNumbers;
    if (role.length) uiFilters.role = role;
    if (status.length) uiFilters.status = status;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        usersActiveTableToggle,
        filterUserId: toFilterItem('Id', id),
        filterUsername: toFilterItem('FullName', fullName),
        filterUserBranch: toFilterItem('BranchName', branchName),
        filterUserOwner: toFilterItem('OwnerName', ownerName),
        filterTrunkNumber: toFilterItem('TrunkNumbers', trunkNumbers),
        filterUserRole: toFilterList('Roles', role),
        filterUserStatusId: toFilterList('StatusId', status),
        uiFilters,
    };
}

export function buildUsersSearchParams(state: UsersUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainUsersSearchParams(state), USERS_ENCODED_QUERY_KEY);
}

function buildPlainUsersSearchParams(state: UsersUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.usersActiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterUserId?.value);
    setScalar(params, 'fullName', state.filterUsername?.value);
    setScalar(params, 'branchName', state.filterUserBranch?.value);
    setScalar(params, 'ownerName', state.filterUserOwner?.value);
    setScalar(params, 'trunkNumbers', state.filterTrunkNumber?.value);
    setMulti(params, 'role', state.filterUserRole);
    setMulti(params, 'status', state.filterUserStatusId);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: UsersFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): UsersFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): UsersFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
